import type { ReactNode } from 'react';
import { create } from 'zustand';

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  /** Defaults to "Confirm" / "Delete" (destructive). */
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** `data-testid` of the confirm button (end-to-end specs press it by id). */
  confirmTestId?: string;
}

interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (confirmed: boolean) => void;
}

interface ConfirmState {
  current: PendingConfirm | null;
  queue: PendingConfirm[];
  push: (item: PendingConfirm) => void;
  /** Resolves the dialog `id` (ignored when that dialog was already settled). */
  settle: (id: number, confirmed: boolean) => void;
}

let nextId = 1;

export const useConfirmStore = create<ConfirmState>()((set, get) => ({
  current: null,
  queue: [],
  push: (item) => {
    if (get().current) set((s) => ({ queue: [...s.queue, item] }));
    else set({ current: item });
  },
  settle: (id, confirmed) => {
    const { current, queue } = get();
    if (current?.id !== id) return;
    current.resolve(confirmed);
    const [next = null, ...rest] = queue;
    set({ current: next, queue: rest });
  },
}));

/**
 * Imperative confirmation: `if (await confirm({ title, destructive: true })) remove()`.
 * Requires `<ConfirmDialogHost />` (mounted once by the app shell).
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    useConfirmStore.getState().push({ ...options, id: nextId++, resolve });
  });
}
